import type { AppearanceColors } from '../../../../generated/graphql-manager';

import { InputDescription, PrimaryButton, SecondaryButton } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';

import { ColorPicker } from './color-picker';
import { Theme } from '../../../../generated/graphql-manager';

import styles from './theme-and-color.module.css';

export type ColorKeys = Exclude<keyof AppearanceColors, '__typename' | 'theme'>;
export type ColorSet = { theme: Theme } & Record<ColorKeys, string>;

type Props = {
  theme: Theme;
  appearanceColors: ColorSet[];
  errors: ColorSet[];
  onColorChange: (name: ColorKeys, value: string) => void;
  onInputBlur: (name: ColorKeys) => void;
  onThemeChange: (theme: Theme) => void;
};

export function ThemeAndColor({ theme, appearanceColors, errors, onThemeChange, onColorChange, onInputBlur }: Props) {
  const { t } = useTranslation();

  const DarkButton = theme === Theme.DARK ? PrimaryButton : SecondaryButton;
  const LightButton = theme === Theme.LIGHT ? PrimaryButton : SecondaryButton;

  const themeColors = appearanceColors.find((colorSet) => colorSet.theme === theme);
  const themeErrors = errors.find((colorSet) => colorSet.theme === theme);

  if (!themeColors || !themeErrors) return null;

  return (
    <div key={theme} className={styles.wrapper}>
      <div className={styles.buttons}>
        <DarkButton data-testid="theme-button-dark" small onClick={() => onThemeChange(Theme.DARK)}>
          {t(`manager.branding.themeAndColor.${Theme.DARK}`)}
        </DarkButton>

        <LightButton data-testid="theme-button-light" small onClick={() => onThemeChange(Theme.LIGHT)}>
          {t(`manager.branding.themeAndColor.${Theme.LIGHT}`)}
        </LightButton>
      </div>

      <h2>{t('manager.branding.themeAndColor.paragraph')}</h2>

      <InputDescription
        inputId="primaryColor"
        label={t('manager.branding.themeAndColor.primaryColor')}
        errorMessage={themeErrors.primaryColor}
      >
        <ColorPicker
          data-testid="primary-color-picker"
          id="primaryColor"
          value={themeColors.primaryColor}
          onInput={(value) => onColorChange('primaryColor', value)}
          onBlur={() => onInputBlur('primaryColor')}
        />
      </InputDescription>

      <InputDescription
        inputId="cardColor"
        label={t('manager.branding.themeAndColor.cardColor')}
        errorMessage={themeErrors.cardColor}
      >
        <ColorPicker
          data-testid="card-color-picker"
          id="cardColor"
          value={themeColors.cardColor}
          onInput={(value) => onColorChange('cardColor', value)}
          onBlur={() => onInputBlur('cardColor')}
        />
      </InputDescription>

      <InputDescription
        inputId="backgroundColor"
        label={t('manager.branding.themeAndColor.backgroundColor')}
        errorMessage={themeErrors.backgroundColor}
      >
        <ColorPicker
          data-testid="background-color-picker"
          id="backgroundColor"
          value={themeColors.backgroundColor}
          onInput={(value) => onColorChange('backgroundColor', value)}
          onBlur={() => onInputBlur('backgroundColor')}
        />
      </InputDescription>

      <InputDescription
        inputId="iconColor"
        label={t('manager.branding.themeAndColor.iconColor')}
        errorMessage={themeErrors.iconColor}
      >
        <ColorPicker
          data-testid="iconColor-color-picker"
          id="iconColor"
          value={themeColors.iconColor}
          onInput={(value) => onColorChange('iconColor', value)}
          onBlur={() => onInputBlur('iconColor')}
        />
      </InputDescription>

      <InputDescription
        inputId="headingFontColor"
        label={t('manager.branding.themeAndColor.headingFontColor')}
        errorMessage={themeErrors.headingFontColor}
      >
        <ColorPicker
          data-testid="heading-font-color-picker"
          id="headingFontColor"
          value={themeColors.headingFontColor}
          onInput={(value) => onColorChange('headingFontColor', value)}
          onBlur={() => onInputBlur('headingFontColor')}
        />
      </InputDescription>

      <InputDescription
        inputId="textFontColor"
        label={t('manager.branding.themeAndColor.textFontColor')}
        errorMessage={themeErrors.textFontColor}
      >
        <ColorPicker
          data-testid="text-font-color-picker"
          id="textFontColor"
          value={themeColors.textFontColor}
          onInput={(value) => onColorChange('textFontColor', value)}
          onBlur={() => onInputBlur('textFontColor')}
        />
      </InputDescription>
    </div>
  );
}
